import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    LoadingOutlined,
    SyncOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    List,
    message,
    Modal,
    Steps,
    Typography,
} from 'antd';
import { Dayjs } from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;
const { Step } = Steps;

interface SplitOrderPublishAllModalProps {
    visible: boolean;
    onCancel: (shouldStopPolling?: boolean) => void;
    selectedTaskDate: Dayjs | null;
    warehouseId: number;
    tableData: any[];
    onStartPublish: () => Promise<void>;

    isAsyncPublishing: boolean;
    asyncPublishProgress?: {
        taskId?: string;
        status?: 'PUBLISHING' | 'COMPLETED' | 'FAILED' | 'UNKNOWN';
        message?: string;
    };
    validationErrors?: string[];
}

enum DisplayPhase {
    VALIDATION = 1,
    TASK_STARTING = 2,
    TASK_RUNNING = 3,
}

const SplitOrderPublishAllModal: React.FC<SplitOrderPublishAllModalProps> = ({
    visible,
    onCancel,
    onStartPublish,
    isAsyncPublishing,
    asyncPublishProgress,
    validationErrors = [],
}) => {
    const { t } = useTranslation();
    const [currentPhase, setCurrentPhase] = useState<DisplayPhase>(DisplayPhase.VALIDATION);
    const [hasStarted, setHasStarted] = useState(false);
    const [startTaskError, setStartTaskError] = useState<string>('');
    const [isRetrying, setIsRetrying] = useState(false);
    const [autoCloseCountdown, setAutoCloseCountdown] = useState<number | null>(null);

    useEffect(() => {
        if (visible) {
            setCurrentPhase(DisplayPhase.VALIDATION);
            setHasStarted(false);
            setStartTaskError('');
            setIsRetrying(false);
            setAutoCloseCountdown(null);
        }
    }, [visible]);

    useEffect(() => {
        if (visible && !hasStarted && validationErrors.length === 0 && !startTaskError) {
            setHasStarted(true);
            setCurrentPhase(DisplayPhase.TASK_STARTING);
            setStartTaskError('');

            onStartPublish().catch((error) => {
                setHasStarted(false);
                const graphQLErrors = error?.graphQLErrors || [];
                const first = graphQLErrors[0];
                const messageKey = first?.extensions?.messageKey;
                const details = first?.extensions?.details || {};
                let errorMessage = error?.message || t('delivery.routes.message.unknownError');
                if (messageKey === 'delivery.routes.publish.duplicateDriverInSegments') {
                    const routeNames = Array.isArray(details.duplicateRouteNames)
                        ? details.duplicateRouteNames.join(', ')
                        : (details.duplicateRouteNames ?? '');
                    errorMessage = t(messageKey, { routeNames });
                } else if (messageKey) {
                    const translated = String(t(messageKey, details));
                    if (translated !== messageKey) {
                        errorMessage = translated;
                    }
                }
                if (errorMessage.includes('502')) {
                    errorMessage = t('delivery.routes.message.serverTemporarilyUnavailable');
                    setIsRetrying(true);
                    setTimeout(() => {
                        setIsRetrying(false);
                        setStartTaskError('');
                        setHasStarted(false);
                    }, 3000);
                }
                if (!errorMessage.includes('502')) {
                    setStartTaskError(errorMessage);
                }
            });
        }
    }, [visible, validationErrors, hasStarted, onStartPublish, startTaskError, t]);

    useEffect(() => {
        if (!visible) return;

        if (asyncPublishProgress) {
            const { status } = asyncPublishProgress;

            if (status === 'PUBLISHING') {
                message.info(t('delivery.routes.message.publishTaskStarted'));
                setTimeout(() => onCancel(false), 1000);
            }
        }
    }, [visible, isAsyncPublishing, asyncPublishProgress, onCancel, t]);

    // 错误时自动关闭
    useEffect(() => {
        const phaseInfo = getPhaseInfo();
        const hasError = phaseInfo.status === 'error';

        if (hasError && autoCloseCountdown === null) {
            setAutoCloseCountdown(3);

            const timer = setInterval(() => {
                setAutoCloseCountdown((prev) => {
                    if (prev === null || prev <= 1) {
                        clearInterval(timer);
                        onCancel(true);
                        return null;
                    }
                    return prev - 1;
                });
            }, 1000);

            return () => clearInterval(timer);
        }

        if (!hasError && autoCloseCountdown !== null) {
            setAutoCloseCountdown(null);
        }
    }, [currentPhase, validationErrors, startTaskError, asyncPublishProgress, onCancel]);

    const getPhaseInfo = () => {
        if (validationErrors.length > 0) {
            return {
                phase: DisplayPhase.VALIDATION,
                status: 'error' as const,
                title: t('delivery.routes.label.validationFailed'),
                description: t('delivery.routes.message.foundValidationErrors', { count: validationErrors.length }),
            };
        }

        if (startTaskError && !isRetrying) {
            return {
                phase: DisplayPhase.TASK_STARTING,
                status: 'error' as const,
                title: t('delivery.routes.label.failedToStartTask'),
                description: startTaskError,
            };
        }

        if (asyncPublishProgress?.status === 'FAILED') {
            return {
                phase: currentPhase,
                status: 'error' as const,
                title: t('delivery.routes.label.validationFailed'),
                description: asyncPublishProgress.message || t('delivery.routes.message.taskFailed'),
            };
        }

        switch (currentPhase) {
            case DisplayPhase.VALIDATION:
                return {
                    phase: DisplayPhase.VALIDATION,
                    status: 'process' as const,
                    title: t('delivery.routes.label.validating'),
                    description: t('delivery.routes.message.checkingRouteConfigurations'),
                };
            case DisplayPhase.TASK_STARTING:
                if (isRetrying) {
                    return {
                        phase: DisplayPhase.TASK_STARTING,
                        status: 'process' as const,
                        title: t('delivery.routes.label.retrying'),
                        description: t('delivery.routes.message.serverUnavailableRetrying'),
                    };
                }
                return {
                    phase: DisplayPhase.TASK_STARTING,
                    status: 'process' as const,
                    title: t('delivery.routes.label.startingTask'),
                    description: t('delivery.routes.message.launchingPublishTask'),
                };
            case DisplayPhase.TASK_RUNNING:
                return {
                    phase: DisplayPhase.TASK_RUNNING,
                    status: 'process' as const,
                    title: t('delivery.routes.label.publishingRoutes'),
                    description: t('delivery.routes.message.creatingDeliveryTasks'),
                };
            default:
                return {
                    phase: DisplayPhase.VALIDATION,
                    status: 'wait' as const,
                    title: t('delivery.routes.label.preparing'),
                    description: t('delivery.routes.message.initializing'),
                };
        }
    };

    const phaseInfo = getPhaseInfo();
    const hasError = phaseInfo.status === 'error';

    const renderValidationErrors = () => (
        <Alert
            message={t('delivery.routes.label.validationFailed')}
            description={
                <List
                    size="small"
                    dataSource={validationErrors}
                    renderItem={(error) => (
                        <List.Item style={{ padding: '2px 0' }}>
                            <Text type="danger">{error}</Text>
                        </List.Item>
                    )}
                />
            }
            type="error"
            showIcon
        />
    );

    const renderPublishProgress = () => (
        <div>
            <Steps
                current={Math.max(0, phaseInfo.phase - 1)}
                direction="vertical"
                size="small"
            >
                {[
                    { phase: DisplayPhase.VALIDATION, title: t('delivery.routes.label.validation'), desc: t('delivery.routes.message.checkingConfigurations') },
                    { phase: DisplayPhase.TASK_STARTING, title: t('delivery.routes.label.startingTask'), desc: t('delivery.routes.message.launchingPathPlanningTask') },
                    { phase: DisplayPhase.TASK_RUNNING, title: t('delivery.routes.label.publishing'), desc: t('delivery.routes.message.creatingDeliveryTasks') },
                ].map(({ phase, title, desc }) => {
                    const isCurrent = phase === phaseInfo.phase;
                    const isPast = phase < phaseInfo.phase;

                    let stepStatus: 'wait' | 'process' | 'finish' | 'error' = 'wait';
                    if (hasError && isCurrent) {
                        stepStatus = 'error';
                    } else if (isCurrent) {
                        stepStatus = phaseInfo.status;
                    } else if (isPast) {
                        stepStatus = 'finish';
                    }

                    return (
                        <Step
                            key={phase}
                            title={isCurrent ? phaseInfo.title : title}
                            description={isCurrent ? phaseInfo.description : desc}
                            status={stepStatus}
                            icon={
                                stepStatus === 'process' ?
                                    (phase === DisplayPhase.TASK_RUNNING ? <SyncOutlined spin /> : <LoadingOutlined />) :
                                stepStatus === 'finish' ? <CheckCircleOutlined /> :
                                stepStatus === 'error' ? <CloseCircleOutlined /> : undefined
                            }
                        />
                    );
                })}
            </Steps>

            {hasError && (
                <Alert
                    message={t('delivery.routes.label.errorDetails')}
                    description={phaseInfo.description}
                    type="error"
                    showIcon
                    style={{ marginTop: 16 }}
                />
            )}
        </div>
    );

    const getModalTitle = () => {
        if (hasError) return t('delivery.routes.label.publishingFailed');
        if (currentPhase === DisplayPhase.TASK_RUNNING) return t('delivery.routes.label.publishingRoutesModal');
        return t('delivery.routes.label.publishingRoutesSimple');
    };

    const getFooterButtons = () => {
        if (hasError) {
            return [
                <Button key="cancel" onClick={() => onCancel()}>
                    {autoCloseCountdown !== null
                        ? t('delivery.routes.message.closeWithCountdown', { count: autoCloseCountdown })
                        : t('components.button.cancel')}
                </Button>,
            ];
        }

        return [
            <Button
                key="cancel"
                onClick={() => onCancel()}
                disabled={currentPhase === DisplayPhase.TASK_RUNNING}
            >
                {currentPhase === DisplayPhase.TASK_RUNNING
                    ? t('delivery.routes.label.pleaseWait')
                    : t('components.button.cancel')}
            </Button>,
        ];
    };

    return (
        <Modal
            title={getModalTitle()}
            open={visible}
            onCancel={() => onCancel()}
            footer={getFooterButtons()}
            closable={currentPhase !== DisplayPhase.TASK_RUNNING}
            maskClosable={currentPhase !== DisplayPhase.TASK_RUNNING}
            width={600}
            destroyOnClose={true}
        >
            {validationErrors.length > 0 ?
                renderValidationErrors() :
                renderPublishProgress()
            }
        </Modal>
    );
};

export default SplitOrderPublishAllModal;